import { Box } from '@mui/material';
import type { DynamicInvoicePageInvoice } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { vyDate } from '@vy/date';
import { notDefined } from '@vy/null';
import React from 'react';
import { EditableDateLabel } from '../../../../../../components/new-design/inputs/EditableLabel';
import { DynamicInvoiceDetail } from './DynamicInvoiceDetails';
import { useIsIssueOnEndOfMonthActive } from '../../../../../../hooks/use-is-issue-on-end-of-month-active';
import { INVOICE_TYPES } from '@vy/types';

type DynamicInvoiceIssueDateProps = {
  invoice: DynamicInvoicePageInvoice;
  shouldDisplayIssueDateAsPending: boolean;
  isInvoiceApproved: boolean;
  onIssueDateSave: (newDate: Date) => Promise<void>;
};

export const DynamicInvoiceIssueDate: React.FC<DynamicInvoiceIssueDateProps> = ({
  invoice,
  shouldDisplayIssueDateAsPending,
  isInvoiceApproved,
  onIssueDateSave: onSave,
}) => {
  const issueOnEndOfMonth = useIsIssueOnEndOfMonthActive();
  const isPostPayment = invoice.invoiceType === INVOICE_TYPES.Postpayment;
  const shouldDisplayBillingCycleEndDate = issueOnEndOfMonth && isPostPayment;

  if (shouldDisplayBillingCycleEndDate) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
        <DynamicInvoiceDetail
          label="Issue date"
          value={vyDate(invoice.billingCycle.endTime).format('MMM. D, YY')}
        />
      </Box>
    );
  }

  const displayedIssueDate = invoice.issuedAt ?? invoice.invoicingDate;

  if (notDefined(displayedIssueDate)) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
      <EditableDateLabel
        date={displayedIssueDate}
        label={
          shouldDisplayIssueDateAsPending
            ? vyDate(invoice.invoicingDate).format('MMM. D, YY')
            : vyDate(invoice.issuedAt).format('MMM. D, YY')
        }
        renderDisplayValue={(value: Date) =>
          shouldDisplayIssueDateAsPending ? (
            <span style={{ color: '#78909C' }} className="flex items-center gap-1">
              <VyIcon icon="PendingClock" strokeOpacity={1} />
              {value?.toDateString()}
            </span>
          ) : (
            vyDate(invoice.issuedAt).format('MMM. D, YY')
          )
        }
        onSave={onSave}
        variant="subtitle2"
        sx={{ fontSize: '14px', fontWeight: 400 }}
        displayInline
        labelText="Issue date"
        disabled={isInvoiceApproved}
      />
    </Box>
  );
};
